import { Typography } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import { Link } from "react-router-dom";

import { FeatureFlagged } from "@/Components/FeatureFlagging";
import { Box } from "@/Components/v2/atoms/Box";
import { LoadingButton, OutlinedButton } from "@/Components/v2/atoms/Button";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import { Stack } from "@/Components/v2/atoms/Stack";
import { BoldSpan } from "@/Components/v2/atoms/Typography";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";

import ScenarioApprovalsNavigationBar from "../../components/ScenarioApprovalsNavigationBar";
import DisclaimerBanner from "./components/DisclaimerBanner";
import HistoricSpendChange from "@/pages/scenario-builder/v3/components/HistoricSpendChange/HistoricSpendChange";
import MediaSpendConstraints from "./MediaSpendConstraints";
import { useScenarioForms } from "./ScenarioFormProvider";
import ScenarioInformationForm from "./ScenarioInformationForm";
import ScenarioTotalBudgetForm from "./ScenarioTotalBudgetForm";
import { SpendInput, StatusEnum } from "./types";
import { scenarioFormText } from "./utils/constants";

const SpendAllocationTable = ({ spendAllocation }: { spendAllocation: SpendInput[] }) => {
  return (
    <table>
      <thead>
        <tr>
          <th style={{ textAlign: "left" }}>Brand</th>
          <th style={{ textAlign: "left" }}>Category</th>
          <th style={{ textAlign: "left" }}>Product</th>
          <th style={{ textAlign: "left" }}>Channel</th>
          <th style={{ textAlign: "left" }}>Geography</th>
          <th style={{ textAlign: "left" }}>Spend</th>
        </tr>
      </thead>
      <tbody>
        {spendAllocation.map(({ brand, category, product, channel, geography, spend }, index) => (
          <tr key={index}>
            <td>{brand}</td>
            <td>{category}</td>
            <td>{product}</td>
            <td>{channel}</td>
            <td>{`${geography}`}</td>
            <td>{spend}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const ScenarioBuilderForm: React.FC = () => {
  const { value: scenarioDebugMode } = useFeatureGate(featureFlag.scenarioDebugMode);
  const { value: scenarioBuilderEnableNewConstraints } = useFeatureGate(
    featureFlag.scenarioBuilderConstraintsFromApi
  );
  const {
    scenarioInformationForm,
    budgetAllocationForm,
    mediaConstraintsForm,
    spendInputForm,
    loading,
    scenario,
    budgetAllocation,
    isSubmitting,
    distinctBCP,
    handleRunScenario,
  } = useScenarioForms();

  const navigationBarProps = {
    scenarioId: scenario?.uuid ?? "",
    scenarioName: "Scenario Form",
    scenarioDescription: scenarioFormText,
    scenarioStatus: scenario?.scenarioStatus as StatusEnum,
    isActivityLogOpen: false,
    handleUpdateScenario: () => {},
    setIsActivityLogOpen: () => {},
  };

  const { startDate, endDate } = scenarioInformationForm.watch();

  const areDatesSelectedOrLoading = startDate && endDate;

  if (loading) {
    return <CircularProgress size={150} sx={{ marginTop: "30rem" }} />;
  }

  return (
    <Box>
      <Stack spacing={2} paddingX={4} paddingTop={2}>
        <ScenarioApprovalsNavigationBar {...navigationBarProps} />
        <ScenarioInformationForm />
        <ScenarioTotalBudgetForm />
        <MediaSpendConstraints disabled={!areDatesSelectedOrLoading} />
        <FeatureFlagged featureName="scenarioDebugSpendAllocationView">
          <h3>Full Spend Allocation</h3>
          <SpendAllocationTable spendAllocation={budgetAllocation ?? []} />
        </FeatureFlagged>
        {scenarioBuilderEnableNewConstraints && (
          <HistoricSpendChange
            scenario={scenario}
            distinctBCP={distinctBCP ? Object.fromEntries(distinctBCP) : undefined}
          />
        )}
        <Stack spacing={2}>
          <DisclaimerBanner />
          <Stack alignItems="flex-end" spacing={1} paddingTop={1} paddingBottom={5}>
            <Stack direction="row" spacing={2}>
              <Link to="/scenarios-v3/all">
                <OutlinedButton variant="text" size="large" sx={{ fontWeight: 600 }}>
                  Save and Exit
                </OutlinedButton>
              </Link>

              <LoadingButton
                size="large"
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={handleRunScenario}
              >
                Submit Scenario
              </LoadingButton>
            </Stack>

            <Typography variant="body2" sx={{ pt: 1 }}>
              <BoldSpan>Note:</BoldSpan> Once submitted, you cannot edit your scenario.
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {scenarioDebugMode && (
        <pre style={{ background: "#f5f5f5", padding: "10px", fontSize: "12px" }}>
          {JSON.stringify(
            {
              scenarioInformationErrors: scenarioInformationForm.formState.errors,
              budgetAllocationErrors: budgetAllocationForm.formState.errors,
              mediaConstraintsErrors: mediaConstraintsForm.formState.errors,
              spendInputErrors: spendInputForm.formState.errors,
            },
            null,
            2
          )}
        </pre>
      )}
    </Box>
  );
};

export default ScenarioBuilderForm;
